// TES-1864/1881/1969/1962: der KANONISCHE Mangel-Dialog — seit TES-1962 app-weit verdrahtet
// (Defects.tsx, Tasks.tsx, ItemDefectsList.tsx); StandaloneDefectDialog ist entfernt.
// CRAP ist 0%-coverage-inflationiert (Edge/fallow vitest-only); Verhalten ist unit-getestet (Repo-Muster, vgl. useTasksQuery/AssignmentCell).
// fallow-ignore-file complexity
// TES-1864 (v2.3 §7) — EIN Basis-Dialog für Mangel View + Edit (Konsolidierung).
// Ein Dialog mit mode 'view' | 'edit'-Toggle. View = read-only (DefectDetailContent); Edit schaltet die
// v2.3-Felder frei: Fachabteilung-Gruppe (Pflicht) + optional Bearbeiter via AssignmentCell (TES-1881),
// "Ich übernehme" Self-Register (TES-1860), StatusTransitionControl, plus Verlinkung + Historie-Tab (TES-1862).
// Die Picker laufen als Layered Modal (useFlowSurface) mit QuickCreate aus @plasser/ui — kein Eigenbau.
//
// TES-1969 — Edit-Modus ist jetzt der echte View+Edit-Superset ggü. StandaloneDefectDialog: Feld-Edit
// (Titel/Beschreibung/Schweregrad/Fotos/Fälligkeit) via DefectFormBody + batched Save
// (useUpdateDefectMutation), Team-Reassign, QM-Override (Ersteller), Cancel-Defect (eigene
// CancelDefectDialog-Instanz statt delegiertem onCancel-Prop — gleiches Endverhalten, einfacher für
// künftige Caller) und Benachrichtigungen (Status-/Zuweisungs-Wechsel) auf den bestehenden Sofort-Schreib-
// Handlern (persist/pickAssignee/handleTransition). Gruppe/Bearbeiter/Status bleiben unverändert Sofort-
// Schreib-Picker (TES-1864/1881/1860) — nur die übrigen Felder sind batched. Parity-Matrix im PR-Body.
//
// Edit-Gate = has_permission_scoped (UI-Gate; RLS bleibt Wahrheit). Nicht-Berechtigte sehen View ohne Edit.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { de, enUS } from "date-fns/locale";
import { Pencil, Eye, Save, Loader2, Ban, Undo2 } from "lucide-react";
import {
  type AssignmentValue,
  EntityPicker,
  LayeredModalHost,
  StatusTransitionControl,
  useFlowSurface,
  type EntityRecord,
  type SearchProvider,
} from "@plasser/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  getGroupPickerConfig,
  getPeoplePickerConfig,
  createSupabaseSearchProvider,
} from "@/components/ui/pickers";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useDefectSelfcorrect, defectSelfcorrectKeys } from "@/hooks/useDefectSelfcorrect";
import { useActiveTeam } from "@/hooks/useActiveTeam";
import { useAssignableProfilesForDefects } from "@/hooks/useAssignableProfiles";
import { useGroupMembers } from "@/hooks/useGroupMembers";
import { useIsGroupOwner } from "@/hooks/useIsGroupOwner";
import { useUpdateDefectMutation } from "@/hooks/useDefectMutations";
import { ProfileSelect } from "@/components/common/ProfileSelect";
import { DueDatePicker } from "@/components/common/DueDatePicker";
import { TeamReassignSelect } from "@/components/common/TeamReassignSelect";
import {
  notifyAssignment,
  notifyCreatorOfAssignment,
  notifyDefectStatusChanged,
} from "@/lib/notifications";
import { logger } from "@/lib/logger";
import { defectsKeys, type DefectWithDetails } from "@/hooks/useDefectsQuery";
import { itemDefectsKeys } from "@/hooks/useItemDefectsQuery";
import type { DefectSeverity, DefectStatus } from "@/types";
import { DEFECT_TRANSITIONS } from "@/config/defectTransitions";
import { DetailGrid, DetailGridAside, DetailGridMain } from "@/components/ui/detail-layout";
import { DefectDetailAside, DefectDetailMain } from "./DefectDetailContent";
import { DefectFormBody } from "./DefectFormBody";
import { DefectGroupAssignment } from "./DefectGroupAssignment";
import { DefectHistoryTab, defectHistoryKeys } from "./DefectHistoryTab";
import { CancelDefectDialog } from "./CancelDefectDialog";
import { NoAssignmentConfirmDialog } from "./NoAssignmentConfirmDialog";
import { appendImagePreviewFiles, removeIndexedStateItem } from "./defectImagePreviews";

const MAX_IMAGES = 5;

export interface DefectBaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defect: DefectWithDetails;
  /** Fachabteilung-Gruppe (id+name) des Mangels (Pflicht-Feld). */
  assignedGroup?: AssignmentValue | null;
  /** UI-Edit-Gate (has_permission_scoped / Gruppen-Mitglied). RLS bleibt die Wahrheit. */
  canEdit?: boolean;
  /** Self-Register sichtbar (Caller ist Gruppen-Mitglied, kein Bearbeiter gesetzt). */
  canSelfAssign?: boolean;
  initialMode?: "view" | "edit";
  onChanged?: () => void;
}

export function DefectBaseDialog(props: DefectBaseDialogProps) {
  return (
    // TES-2034: Drawer-auf-Mobile via ResponsiveDialog (Wave-Audit-Finding — der kanonische
    // Mangel-Dialog war der einzige Kern-Flow, der noch den zentrierten Dialog auf Mobile
    // zeigte, im Widerspruch zum Schwester-Flow DefectReportDialog).
    // TES-2042: gleiche Shell-Breite wie die Detail-Ansicht (Defects.tsx View-Dialog,
    // max-w-6xl) — View-Tab und Edit-Formular sprechen dieselben Proportionen, der
    // DefectDetailContent wird nicht mehr in eine schmalere Shell gequetscht.
    // TES-2250: stabiler Flex-Shell mit FESTER Rahmenhöhe (h-[85dvh]) statt content-
    // getriebener Auto-Höhe — der Rahmen bleibt beim Details↔Historie-Tabwechsel konstant
    // (der Inhalt scrollt innerhalb, nicht der Rahmen). TES-2288: `md:`-Präfix (nicht `sm:`!)
    // hält die feste Breite/Höhe auf die Desktop-Dialog-Variante beschränkt — ResponsiveDialog
    // schaltet erst bei 768px (MOBILE_BREAKPOINT) auf Dialog um, `sm:` feuert aber schon ab
    // 640px und würde auf 640–767px-Viewports den Bottom-DRAWER mit erzwungener Höhe und
    // links-verankertem Breiten-Cap treffen. Die mobile Drawer-Variante bleibt so bei
    // ResponsiveDialogs eigener max-h-[90dvh] + natürlicher voller Breite.
    <ResponsiveDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      className="md:h-[85dvh] md:w-[calc(100%-2rem)] md:max-w-6xl flex flex-col overflow-hidden"
    >
      <LayeredModalHost>
        <DefectBaseDialogInner {...props} />
      </LayeredModalHost>
    </ResponsiveDialog>
  );
}

function DefectBaseDialogInner({
  defect,
  assignedGroup = null,
  canEdit = false,
  canSelfAssign = false,
  initialMode = "view",
  onChanged,
}: DefectBaseDialogProps) {
  const { t, i18n } = useTranslation();
  const { user, profile, isQualitaetsmanager } = useAuth();
  const queryClient = useQueryClient();
  const dateLocale = i18n.language === "de" ? de : enUS;

  const [mode, setMode] = useState<"view" | "edit">(canEdit ? initialMode : "view");
  const [group, setGroup] = useState<AssignmentValue | null>(assignedGroup);
  const [assignee, setAssignee] = useState<AssignmentValue | null>(
    defect.assigned_to
      ? {
          id: defect.assigned_to,
          name:
            defect.assigned_to_profile?.display_name ||
            [defect.assigned_to_profile?.first_name, defect.assigned_to_profile?.last_name]
              .filter(Boolean)
              .join(" ") ||
            defect.assigned_to_profile?.email ||
            defect.assigned_to,
        }
      : null,
  );
  const [groupError, setGroupError] = useState(false);
  // Tracked locally (not read from `defect.status`) for the same reason group/assignee are: the
  // `defect` prop never updates within a session (no page-wiring caller yet, TES-1962), so reading
  // it after an immediate-write transition would show/save a stale value — see handleTransition().
  const [status, setStatus] = useState<DefectStatus>(defect.status);

  // TES-1969 — batched full-edit form state (title/description/severity/photos/due-date/team/
  // created-by). Group, assignee and status stay on the pre-existing immediate-write pickers above
  // (unchanged) — only these fields are staged locally and committed together via one Save.
  const { activeTeamId } = useActiveTeam();
  const { data: qmProfiles = [] } = useAssignableProfilesForDefects(
    activeTeamId,
    isQualitaetsmanager && mode === "edit",
  );
  const updateDefectMutation = useUpdateDefectMutation();
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(defect.title);
  // TES-1733 (D2): Inline-Pflichtfeld-Fehler am Titel (Muster TES-1724/#650), nicht nur Toast.
  const [titleError, setTitleError] = useState<string | null>(null);
  const [description, setDescription] = useState(defect.description || "");
  const [severity, setSeverity] = useState<DefectSeverity>(defect.severity);
  const [existingImages, setExistingImages] = useState<string[]>(defect.images || []);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<Date | undefined>(
    defect.due_date ? new Date(defect.due_date) : undefined,
  );
  const [teamId, setTeamId] = useState<string>(defect.team_id || "");
  const [createdBy, setCreatedBy] = useState<string>(defect.created_by || "");
  const [showNoDueDateConfirm, setShowNoDueDateConfirm] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [liftingCancellation, setLiftingCancellation] = useState(false);

  // TES-2229 S8/S5: Server-Verdict (rpc_defect_selfcorrect_status) — dieselben Prädikate wie
  // rpc_cancel_defect/rpc_reopen_defect. Gated die Storno-/„Storno aufheben"-Affordances
  // (Ersteller-im-Fenster ∨ QM ∨ Verifizierer/Deputy statt des alten reinen Ersteller-Checks).
  const { data: selfcorrect } = useDefectSelfcorrect(defect.id);
  const mayCancel = selfcorrect
    ? selfcorrect.may_cancel
    : user?.id === defect.created_by && !["cancelled", "closed"].includes(status);

  // Re-hydrate the batched draft from server truth every time edit mode is (re-)entered — this also
  // doubles as "discard unsaved changes" when the user toggles back to view without saving and
  // re-opens edit, and picks up the freshly-saved values after a successful save (see saveEdit()).
  useEffect(() => {
    if (mode !== "edit") return;
    setTitle(defect.title);
    setTitleError(null);
    setDescription(defect.description || "");
    setSeverity(defect.severity);
    setExistingImages(defect.images || []);
    setImageFiles([]);
    setImagePreviews([]);
    setDueDate(defect.due_date ? new Date(defect.due_date) : undefined);
    setTeamId(defect.team_id || "");
    setCreatedBy(defect.created_by || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, defect.id]);

  const addFiles = (files: File[]) => {
    appendImagePreviewFiles({
      files,
      currentImageCount: existingImages.length + imageFiles.length,
      maxImages: MAX_IMAGES,
      setImageFiles,
      setImagePreviews,
    });
  };

  const removeNewImage = (index: number) => {
    removeIndexedStateItem(setImageFiles, index);
    removeIndexedStateItem(setImagePreviews, index);
  };

  const removeExistingImage = (index: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveEdit = () => {
    if (!title.trim()) {
      // TES-1733 (D2): Feld rot rahmen + FieldError inline, Toast bleibt als Summary.
      setTitleError(t("defects.dialog.titleRequired"));
      toast.error(t("defects.dialog.titleRequired"), {
        description: t("defects.dialog.titleRequiredDesc"),
      });
      return;
    }
    if (!group) {
      setGroupError(true);
      toast.error(t("defects.assignmentSection.groupRequired"));
      return;
    }
    if (!dueDate) {
      setShowNoDueDateConfirm(true);
      return;
    }
    void saveEdit();
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await updateDefectMutation.mutateAsync({
        id: defect.id,
        title,
        description: description || null,
        severity,
        status,
        assignedTo: assignee?.id ?? null,
        dueDate: dueDate ? dueDate.toISOString() : null,
        teamId: teamId || undefined,
        createdBy: isQualitaetsmanager && createdBy ? createdBy : undefined,
        imageFiles,
        imagePreviews,
        existingImages,
      });
      toast.success(t("defects.dialog.updated"), {
        description: t("defects.dialog.updatedDesc"),
      });
      setMode("view");
      invalidate();
    } catch {
      logger.error("Defect edit save failed", { defectId: defect.id });
      toast.error(t("common.error"), { description: t("defects.dialog.saveError") });
    } finally {
      setSaving(false);
    }
  };

  const provider = useMemo(
    () =>
      createSupabaseSearchProvider({
        userId: user?.id,
        activeTeamId: profile?.active_team_id ?? undefined,
      }),
    [user?.id, profile?.active_team_id],
  );

  // TES-1990 (v2.3 F-RBAC-OFFB-05): Gruppen-OWNER = Mangelzuweiser seiner Gruppe. Die Affordance
  // erscheint NUR, wenn der reguläre Edit-Pfad geschlossen ist (canEdit=false) — QM & Co. nutzen
  // weiter die AssignmentCell. Zuweisung läuft über den spalten-engen RPC defect_owner_assign
  // (Mig 369; Ziel muss Gruppen-Mitglied sein), nie über den direkten Tabellen-UPDATE.
  const { isOwner } = useIsGroupOwner(group?.id ?? null, user?.id ?? null, !canEdit);
  const canOwnerAssign = isOwner && !canEdit;
  // TES-2102 (R-UI-INCONTEXT-01): der reguläre Bearbeiter-Picker (pickAssignee, QM-Edit-Pfad) MUSS
  // ebenfalls auf effektive Gruppen-Mitglieder beschränkt sein (AC: "keine gruppenfremden Personen"),
  // nicht nur der Owner-Assign-Pfad — daher auch im Edit-Modus laden, nicht nur für canOwnerAssign.
  // HINWEIS (Out-of-Scope-Fund, R-SCOPE-01): anders als bei inspections/inspection_responses
  // (validate_assignee_group_membership, Mig 358) gibt es für defects.assigned_to KEINEN
  // analogen DB-Trigger — dieser Filter ist aktuell UI-only (defense bleibt unvollständig ggü.
  // einem direkten REST-Write). Ob ein server-seitiger Guard nachgezogen wird, ist eine
  // Owner-Entscheidung (kein Migrations-Alleingang hier) — siehe PR-Beschreibung.
  const { members } = useGroupMembers(group?.id ?? null, canOwnerAssign || mode === "edit");
  const memberIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);
  // Der Owner-Picker UND der reguläre Bearbeiter-Picker bieten NUR effektive Gruppen-Mitglieder an:
  // gleiche RLS-gescopte Personensuche, client-seitig auf die Mitglieder-Ids reduziert.
  const memberProvider = useMemo<SearchProvider>(
    () => ({
      search: async (config, query) => {
        const res = await provider.search(config, query);
        const items = res.items.filter((r) => memberIds.has(r.id));
        return { items, total: items.length };
      },
    }),
    [provider, memberIds],
  );

  const groupConfig = useMemo(() => getGroupPickerConfig(t), [t]);
  const personConfig = useMemo(() => getPeoplePickerConfig(t), [t]);

  const groupFlow = useFlowSurface("medium", { title: t("defects.assignmentSection.groupLabel") });
  const peopleFlow = useFlowSurface("medium", {
    title: t("defects.assignmentSection.assigneeLabel"),
  });

  const toValue = (rec: EntityRecord | undefined): AssignmentValue | null =>
    rec ? { id: rec.id, name: rec.name } : null;

  /* istanbul ignore next -- flow-surface picker glue; covered by e2e (R-SYM-04), unit-hard in jsdom */
  const pickGroup = async () => {
    const result = await groupFlow.open<EntityRecord>((api) => (
      <div className="h-[70vh]">
        <EntityPicker
          config={groupConfig}
          provider={provider}
          autoFocus
          onConfirm={(sel) => api.close(sel[0])}
        />
      </div>
    ));
    const val = toValue(result);
    if (val) {
      setGroup(val);
      setGroupError(false);
      await persist({ assigned_group_id: val.id });
    }
  };

  // TES-2102: Bearbeiter-Picker kandidaten-beschränkt auf effektive Gruppen-Mitglieder (UI) —
  // gleicher memberProvider wie pickOwnerAssignee (s.o.), kein gruppenfremder Treffer wählbar.
  /* istanbul ignore next -- flow-surface picker glue; covered by e2e (R-SYM-04), unit-hard in jsdom */
  const pickAssignee = async () => {
    const result = await peopleFlow.open<EntityRecord>((api) => (
      <div className="h-[70vh]">
        <EntityPicker
          config={personConfig}
          provider={memberProvider}
          onConfirm={(sel) => api.close(sel[0])}
        />
      </div>
    ));
    const val = toValue(result);
    if (val) {
      const previousAssignedTo = assignee?.id ?? null;
      setAssignee(val);
      const ok = await persist({ assigned_to: val.id });
      // TES-1969 (parity w/ StandaloneDefectDialog.saveDefect): notify the new assignee, and the
      // creator (unless they assigned themselves), on a real assignee change.
      if (ok && val.id !== previousAssignedTo) {
        notifyAssignment(val.id, "defect", defect.title, defect.id);
        const creatorId = defect.created_by;
        if (creatorId && creatorId !== val.id) {
          notifyCreatorOfAssignment(creatorId, "defect", defect.title, defect.id, val.name);
        }
      }
    }
  };

  const persist = async (patch: {
    // TES-2063: assigned_group_id ist NOT NULL (Mig 362) — die Fachabteilung wird nur auf eine echte
    // Gruppe gesetzt, nie auf null geleert (der Update-Typ erwartet string|undefined, kein null).
    assigned_group_id?: string;
    assigned_to?: string | null;
    status?: DefectStatus;
  }): Promise<boolean> => {
    // { count: "exact" } mirrors useUpdateDefectMutation's zero-row RLS-denial guard
    // (useDefectMutations.ts) — without it, an RLS-blocked write returns no `error` but 0 rows
    // touched, which would otherwise read as success and fire a false assignment/status notification.
    const { error, count } = await supabase
      .from("defects")
      .update(patch, { count: "exact" })
      .eq("id", defect.id);
    if (error || count === 0) {
      toast.error(t("defects.assignmentSection.assignError"));
      return false;
    }
    invalidate();
    return true;
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: defectHistoryKeys.list(defect.id) });
    // Also cover the list/detail caches the immediate-write pickers (group/assignee/status) and
    // CancelDefectDialog touch directly (outside useUpdateDefectMutation, whose own onSuccess only
    // covers the batched-save path) — defense in depth vs. relying solely on the optional onChanged.
    queryClient.invalidateQueries({ queryKey: defectsKeys.all });
    queryClient.invalidateQueries({ queryKey: itemDefectsKeys.all });
    // TES-2229 S8: das Selbstkorrektur-Verdict hängt an Status/Zuweisung/Kommentaren.
    queryClient.invalidateQueries({ queryKey: defectSelfcorrectKeys.forDefect(defect.id) });
    onChanged?.();
  };

  // TES-2229 (LH §L5.10): „Storno aufheben" (reversibel) — server-gated via rpc_reopen_defect
  // (NUR Ersteller/zuständiger Verifizierer; auditiert). Banner-Aktion im cancelled-Zustand.
  const handleLiftCancellation = async () => {
    setLiftingCancellation(true);
    try {
      const { error } = await (
        supabase as unknown as {
          rpc: (
            fn: "rpc_reopen_defect",
            args: { _defect_id: string; _reason: string | null },
          ) => Promise<{ error: { code?: string; message: string } | null }>;
        }
      ).rpc("rpc_reopen_defect", { _defect_id: defect.id, _reason: null });
      if (error) throw error;
      setStatus("open");
      toast.success(t("defects.selfcorrect.reopenSuccess"));
      invalidate();
    } catch (error) {
      logger.error("Error lifting cancellation", error, { defectId: defect.id });
      toast.error(t("common.error"), { description: t("defects.selfcorrect.reopenError") });
    } finally {
      setLiftingCancellation(false);
    }
  };

  const handleSelfAssign = async () => {
    const { error } = await supabase.rpc("defect_self_assign", { _defect_id: defect.id });
    if (error) {
      toast.error(t("defects.assignmentSection.assignError"));
      return;
    }
    setAssignee(
      user?.id
        ? { id: user.id, name: profile?.display_name || profile?.email || user.id }
        : assignee,
    );
    toast.success(t("tasks.maengelOhneBearbeiter.assignSuccess"));
    invalidate();
  };

  // TES-1990: Owner-Zuweisung über den RPC (SECURITY DEFINER, Caller-/Mitglieds-Guards in-body).
  // target=null stellt den Mangel zurück in die Gruppen-Queue. Benachrichtigungs-Parität mit
  // pickAssignee (neuer Bearbeiter + Ersteller werden informiert).
  const applyOwnerAssign = async (target: AssignmentValue | null) => {
    const previousAssignedTo = assignee?.id ?? null;
    const { error } = await supabase.rpc("defect_owner_assign", {
      _defect_id: defect.id,
      ...(target ? { _assignee: target.id } : {}),
    });
    if (error) {
      logger.error("defect_owner_assign failed", error);
      toast.error(t("defects.assignmentSection.assignError"));
      return;
    }
    setAssignee(target);
    if (target) {
      toast.success(t("defects.assignmentSection.ownerAssignSuccess"));
      if (target.id !== previousAssignedTo) {
        notifyAssignment(target.id, "defect", defect.title, defect.id);
        const creatorId = defect.created_by;
        if (creatorId && creatorId !== target.id) {
          notifyCreatorOfAssignment(creatorId, "defect", defect.title, defect.id, target.name);
        }
      }
    } else {
      toast.success(t("defects.assignmentSection.ownerUnassignSuccess"));
    }
    invalidate();
  };

  /* istanbul ignore next -- flow-surface picker glue; covered by e2e (R-SYM-04), unit-hard in jsdom */
  const pickOwnerAssignee = async () => {
    const result = await peopleFlow.open<EntityRecord>((api) => (
      <div className="h-[70vh]">
        <EntityPicker
          config={personConfig}
          provider={memberProvider}
          onConfirm={(sel) => api.close(sel[0])}
        />
      </div>
    ));
    const val = toValue(result);
    if (val) await applyOwnerAssign(val);
  };

  const handleTransition = async (to: DefectStatus) => {
    const previousStatus = status;
    const ok = await persist({ status: to });
    // TES-1969 (parity w/ StandaloneDefectDialog.saveDefect): notify on a real status change —
    // the verifying QM (falling back to the creator) when resolving, otherwise the creator. Uses
    // server-truth defect.verifying_qm/created_by (not the batched-edit draft), since a status
    // transition here is a separate, immediate action, decoupled from the full-edit Save.
    if (ok) {
      setStatus(to);
      if (to !== previousStatus) {
        if (to === "resolved") {
          const recipient = defect.verifying_qm || defect.created_by;
          if (recipient) {
            notifyDefectStatusChanged(recipient, defect.title, defect.id, to, {
              enableTeamsApproval: true,
            });
          }
        } else if (defect.created_by) {
          notifyDefectStatusChanged(defect.created_by, defect.title, defect.id, to);
        }
      }
    }
  };

  return (
    // TES-2250: der innere Rahmen füllt den festen ResponsiveDialog-Shell (flex-1) und ist
    // selbst flex-col — Header/TabsList bleiben fix (shrink-0), nur das aktive Tab-Panel
    // scrollt. So ändert der Tab-Wechsel die Overlay-Größe nicht mehr.
    <div data-testid="defect-base-dialog" className="flex min-h-0 flex-1 flex-col">
      <ResponsiveDialogHeader className="shrink-0 px-4 md:px-6">
        <div className="flex items-center justify-between gap-2 pr-6">
          <ResponsiveDialogTitle>{defect.title}</ResponsiveDialogTitle>
          {canEdit && (
            <Button
              type="button"
              size="sm"
              variant={mode === "edit" ? "secondary" : "outline"}
              onClick={() => setMode(mode === "edit" ? "view" : "edit")}
              disabled={saving}
              aria-pressed={mode === "edit"}
              data-testid="defect-mode-toggle"
            >
              {mode === "edit" ? (
                <>
                  <Eye className="mr-1 h-3.5 w-3.5" />
                  {t("defects.baseDialog.viewMode")}
                </>
              ) : (
                <>
                  <Pencil className="mr-1 h-3.5 w-3.5" />
                  {t("defects.baseDialog.edit")}
                </>
              )}
            </Button>
          )}
        </div>
        <ResponsiveDialogDescription>
          {t("defects.dialog.editDescription")}
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>

      <Tabs defaultValue="details" className="mt-2 flex min-h-0 flex-1 flex-col px-4 md:px-6">
        {/* self-start: im flex-col-Shell die kompakte, links-bündige Tab-Leiste erhalten
            (sonst streckt align-items:stretch sie auf volle Breite). */}
        <TabsList className="shrink-0 self-start">
          {/* TES-2042: "Details" statt "Ansicht" — der Tab zeigt je nach Modus View ODER
              Edit-Formular; "Ansicht" kollidierte mit dem gleichnamigen Mode-Toggle. */}
          <TabsTrigger value="details" data-testid="tab-details">
            {t("common.details")}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            {t("defects.history.title")}
          </TabsTrigger>
        </TabsList>

        {/* TES-2250: flex-1 + eigenes overflow-y-auto — beide Tab-Panels füllen denselben
            festen Rahmen; nur der Panel-Inhalt scrollt, die Overlay-Größe bleibt konstant. */}
        <TabsContent value="details" className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          {/* TES-2229 (Kommentar 2, §L5.10): cancelled-Banner — Grund sichtbar, „Storno aufheben"
              (reversibel) NUR für Ersteller/Verifizierer (Server-Verdict may_reopen; RPC gated). */}
          {status === "cancelled" && (
            <div
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-muted-foreground/30 bg-muted px-3 py-2"
              data-testid="defect-cancelled-banner"
            >
              <p className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                <Ban className="h-4 w-4 shrink-0" aria-hidden />
                <span className="font-medium">{t("defects.cancelledBanner.title")}</span>
                {defect.cancellation_reason && (
                  <span className="truncate">— {defect.cancellation_reason}</span>
                )}
              </p>
              {selfcorrect?.may_reopen && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleLiftCancellation}
                  disabled={liftingCancellation}
                  data-testid="defect-cancelled-banner-reopen"
                >
                  {liftingCancellation ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
                  ) : (
                    <Undo2 className="h-4 w-4 mr-1" aria-hidden />
                  )}
                  {t("defects.selfcorrect.liftCancellation")}
                </Button>
              )}
            </div>
          )}
          {/* TES-2042: EIN DetailGrid für beide Modi — die Aside (System-Info/Lifecycle/SAP/
              Activity-Log) bleibt über den View/Edit-Wechsel hinweg an stabiler Baumposition
              gemountet (kein Remount → kein stiller Verlust eines Kommentar-Entwurfs), und
              beide Modi teilen die 8/4-Proportionen der Detail-Familie (screen-07). */}
          <DetailGrid data-testid="defect-detail-block">
            <DetailGridMain>
              <DefectGroupAssignment
                group={group}
                assignee={assignee}
                canEdit={mode === "edit"}
                canSelfAssign={canSelfAssign}
                canOwnerAssign={canOwnerAssign}
                showGroupError={groupError}
                onPickGroup={pickGroup}
                onPickAssignee={pickAssignee}
                onClearAssignee={() => {
                  setAssignee(null);
                  void persist({ assigned_to: null });
                }}
                onSelfAssign={handleSelfAssign}
                onOwnerAssign={() => void pickOwnerAssignee()}
                onOwnerUnassign={() => void applyOwnerAssign(null)}
              />

              {mode === "edit" && (
                <div className="space-y-1" data-testid="defect-status-control">
                  <StatusTransitionControl
                    status={status}
                    config={DEFECT_TRANSITIONS}
                    onTransition={handleTransition}
                    statusLabel={(s) =>
                      t(`defects.status.${s === "in_progress" ? "inProgress" : s}`, s)
                    }
                  />
                </div>
              )}

              {mode === "edit" ? (
                // TES-1969 — batched full-edit form (title/description/severity/photos/due-date/team/
                // created-by), replacing the read-only detail block while editing. Group/assignee/status
                // stay visible above via the unchanged immediate-write pickers.
                <div className="space-y-4 border-t pt-4" data-testid="defect-full-edit">
                  <DefectFormBody
                    severity={severity}
                    onSeverityChange={setSeverity}
                    title={title}
                    onTitleChange={(v) => {
                      setTitle(v);
                      if (titleError) setTitleError(null);
                    }}
                    titleError={titleError}
                    description={description}
                    onDescriptionChange={setDescription}
                    existingImages={existingImages}
                    onRemoveExistingImage={removeExistingImage}
                    imagePreviews={imagePreviews}
                    onRemoveNewImage={removeNewImage}
                    onAddFiles={addFiles}
                    maxImages={MAX_IMAGES}
                    disabled={saving}
                    inputIdPrefix="defect-base-edit"
                    variant="stacked"
                  >
                    <section className="space-y-2">
                      <Label>{t("defects.dialog.dueDate")}</Label>
                      <DueDatePicker
                        value={dueDate}
                        onChange={setDueDate}
                        locale={dateLocale}
                        placeholder={t("defects.dialog.noDateSelected")}
                      />
                      {dueDate && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDueDate(undefined)}
                          className="text-xs text-muted-foreground"
                        >
                          {t("defects.dialog.removeDate")}
                        </Button>
                      )}
                    </section>

                    <TeamReassignSelect
                      value={teamId}
                      onChange={setTeamId}
                      createdBy={defect.created_by ?? null}
                      currentUserId={user?.id}
                    />

                    {isQualitaetsmanager && (
                      <section className="space-y-2">
                        <Label>{t("defects.dialog.createdBy")}</Label>
                        <ProfileSelect
                          profiles={qmProfiles}
                          value={createdBy}
                          onValueChange={setCreatedBy}
                          placeholder={t("defects.dialog.selectCreator")}
                          disabled={saving}
                        />
                      </section>
                    )}

                    {/* TES-2229 S5: Storno-Affordance server-verdict-gated (Ersteller-im-Fenster ∨
                        QM ∨ Verifizierer/Deputy) — der Fachbereich sieht stattdessen „Storno
                        anregen" in der „Ersteller & Storno"-Card (Aside). Pflicht-Grund im Dialog. */}
                    {mayCancel && !["cancelled", "closed"].includes(status) && (
                      <div className="pt-2">
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => setCancelDialogOpen(true)}
                          disabled={saving}
                          data-testid="defect-cancel-trigger"
                        >
                          {t("defects.cancelDefect")}
                        </Button>
                      </div>
                    )}
                  </DefectFormBody>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setMode("view")}
                      disabled={saving}
                      data-testid="defect-edit-discard"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="button"
                      variant="action"
                      onClick={handleSaveEdit}
                      disabled={saving}
                      data-testid="defect-edit-save"
                      className="gap-2 font-bold shadow-sm transition-all active:scale-95"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" aria-hidden="true" />
                      )}
                      {t("defects.dialog.update")}
                    </Button>
                  </div>
                </div>
              ) : (
                // View = read-only detail main column (reuse; Aside kommt aus dem äußeren Grid).
                <div data-testid="defect-detail-view" className="space-y-4">
                  <DefectDetailMain defect={defect} />
                </div>
              )}
            </DetailGridMain>
            <DetailGridAside>
              <DefectDetailAside defect={defect} />
            </DetailGridAside>
          </DetailGrid>
        </TabsContent>

        <TabsContent value="history" className="min-h-0 flex-1 overflow-y-auto">
          <DefectHistoryTab defectId={defect.id} />
        </TabsContent>
      </Tabs>

      <NoAssignmentConfirmDialog
        open={showNoDueDateConfirm}
        onOpenChange={setShowNoDueDateConfirm}
        title={t("defects.dialog.noDueDateTitle")}
        description={t("defects.dialog.noDueDateDescription")}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("defects.dialog.saveWithoutDate")}
        onConfirm={async () => {
          setShowNoDueDateConfirm(false);
          await saveEdit();
        }}
      />

      <CancelDefectDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        defect={{ id: defect.id, title: defect.title, assigned_to: assignee?.id ?? null }}
        onCancelled={() => {
          setStatus("cancelled");
          setMode("view");
          invalidate();
        }}
      />
    </div>
  );
}
